import { Language } from '@core/interfaces/language';

import { TBotItem } from '@core/interfaces/TBotItem';
import { TSourceDetail } from '@core/interfaces/TMessage';

import {
  EmbeddingContent,
  MessageAuthorV2,
  TAdaptedBotConversationV2,
  TAdaptedMessageV2,
  TAdaptedReferenceV3,
  TConversationType,
} from '../../types/types';
import { ReferencesUtils } from '@shared/utils/referencesUtils';
import { StringUtils } from '@shared/utils/stringUtils';

export class Conversation implements TAdaptedBotConversationV2 {
  id?: string;
  bot?: TBotItem;
  language?: Language;
  content?: string | string[];
  messages: TAdaptedMessageV2[] = [];
  favorite? = false;
  embeddingContent?: EmbeddingContent;
  originalSourceDetails?: TSourceDetail[];
  type: TConversationType = 'conversation';

  constructor(data?: Partial<TAdaptedBotConversationV2>) {
    if (data) {
      Object.assign(this, data);
    }
  }

  addNewMessage(
    author: MessageAuthorV2,
    text?: string,
    date = new Date().toISOString(),
    keywords?: string[]
  ) {
    const msg = {
      date,
      author,
      paragraphs: text === undefined ? [] : text === '' ? [{ text: '' }] : [{ text }],
      keywords,
    } as TAdaptedMessageV2;

    msg.referencesToDisplay = [];
    this.messages.push(msg);
    return msg;
  }

  addNewError(text: string | null = null) {
    this.messages.push({
      date: new Date().toISOString(),
      author: 'assistant',
      paragraphs: [
        { text: ['An error occurred', text, 'Please try again.'].filter(Boolean).join('. ') },
      ],
      type: 'error',
    } as TAdaptedMessageV2);
  }

  addNewRetryMessage() {
    const friendlyMessages = [
      {
        main: 'I\'m having trouble connecting right now. This sometimes happens due to network issues or high demand.',
        action: 'Click \'Try again\' and I\'ll start fresh with your question.',
      },
      {
        main: 'Looks like there\'s a temporary connection hiccup on my end.',
        action: 'No worries though - just hit \'Try again\' and we\'ll get this sorted!',
      },
      {
        main: 'I seem to be having a moment here. Sometimes the internet can be a bit finicky.',
        action: 'Let\'s give it another shot with \'Try again\'.',
      },
      {
        main: 'Oops, I lost my train of thought there for a second.',
        action: 'Click \'Try again\' and I\'ll be right back with you!',
      },
    ];

    const randomMessage = friendlyMessages[Math.floor(Math.random() * friendlyMessages.length)];

    this.messages.push({
      date: new Date().toISOString(),
      author: 'assistant',
      paragraphs: [{ text: randomMessage.main }, { text: randomMessage.action }],
      type: 'retry',
    } as TAdaptedMessageV2);
  }

  getLastMessageByAuthor(author: MessageAuthorV2): TAdaptedMessageV2 | undefined {
    return this.messages
      .slice()
      .reverse()
      .find(message => message.author === author);
  }

  getLastMessage(): TAdaptedMessageV2 | undefined {
    if (this.messages.length) {
      return this.messages[this.messages.length - 1];
    }
    return undefined;
  }

  getReferenceFromMessageBySourceId(reference: TAdaptedReferenceV3, message: TAdaptedMessageV2) {
    return message.referencesToDisplay?.find(ref => ref.originalSource?._id === reference.sourceId);
  }

  getEmbeddingContent(sourceId: string, embeddingId: string) {
    // TOOD: This is a temporary solution to get the embedding content from the originalSourceDetails
    // First try to get from embeddingContent
    const embedding = this.embeddingContent?.[sourceId]?.find(
      content => content.embeddingId === embeddingId
    );
    if (embedding) {
      return embedding.content;
    }

    // Try accumulated sources first (for reference continuity)
    const allSources = (this as any).allSourcesForReferences;
    if (allSources) {
      const sourceDetail = allSources.find(
        (detail: any) => detail._id === sourceId && detail.embeddingID === embeddingId
      );
      if (sourceDetail) {
        return sourceDetail.embeddingContent;
      }
    }

    // Final fallback to originalSourceDetails if no embedding found
    const sourceDetail = this.originalSourceDetails?.find(
      detail => detail._id === sourceId && detail.embeddingID === embeddingId
    );
    return sourceDetail ? sourceDetail.embeddingContent : undefined;
  }

  getLastAgentMessageToString() {
    const lastMessage = this.getLastMessageByAuthor('assistant');
    if (lastMessage) return this.getMessageToString(lastMessage);
    return '';
  }

  getMessageToString(message: TAdaptedMessageV2, includeReferences = false): string {
    let concatenatedText = message.paragraphs
      .map(text => {
        let textWithRefs = StringUtils.sanitizeMarkdown(text.text.trim());
        if (includeReferences) {
          const refs = text.references?.map(ref => `[${ref.listPositionIndex}]`).join(' ');
          textWithRefs = `${textWithRefs.trim()} ${refs}`;
        }
        return textWithRefs.trim() + '\n\n';
      })
      .join('');

    if (includeReferences && message.referencesToDisplay?.length) {
      concatenatedText += `\n${ReferencesUtils.getReferenceLabelForLanguage(this.language)}: \n\n`;
      message.referencesToDisplay.forEach(ref => {
        ref.children?.forEach(child => {
          concatenatedText += `[${child.listPositionIndex}] `;
        });
        concatenatedText += `${ref.originalSource?.name}\n`;
      });
    }

    return concatenatedText;
  }
}
